import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import FormInput from '../../../shared/components/ui/FormInput';
import Multiselect from 'multiselect-react-dropdown';
import Button from '../../../shared/components/ui/Button';
import { getAllAccommodationsByProject } from '../../services/api/implementation/impl';
import {
  getCountryCodeOptions,
  validatePhoneNumber,
  getPhoneNumberLength,
  cleanPhoneNumber,
  getDefaultCountryCode,
  // detectCountryFromPhoneNumber, // No longer used - auto-detection disabled
  getCountryCallingCode
} from '../../utils/phoneNumberUtils';
import { capitalizeWords } from '../../utils/stringUtils';

const createLeadSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  countryCode: yup.string().required('Country code is required'),
  phone_number: yup
    .string()
    .required('Phone number is required')
    .test('phone-validation', 'Please enter a valid phone number', function (value) {
      const { countryCode } = this.parent;
      if (!value || !countryCode) return false;
      return validatePhoneNumber(value, countryCode);
    }),
  alternate_phone_number: yup
    .string()
    .test('optional-phone', 'Please enter a valid alternate phone number', function (value) {
      const { countryCode } = this.parent;
      if (!value || value.trim() === '') {
        return true;
      }
      return validatePhoneNumber(value, countryCode);
    })
    .nullable(),
  occupation: yup.string(),
  address: yup.string(),
  reference_name: yup.string(),
  email: yup.string(),
  gender: yup.string(),
  interested_projects: yup.string().required('Select atleat one project'),
  accomodations: yup.array(),
  plotArea: yup.array(),
  project_accommodations: yup.array()
});

const AddLeadForm = ({
  onSubmit,
  onCancel,
  isSubmitting,
  AllProjects,
  defaultProjectId = null,
  lockProject = false,
  initialValues = null,
  preLeadId = null
}) => {
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    initialValues?.countryCode || getDefaultCountryCode()
  );
  const [isAdditionalInfoCollapsed, setIsAdditionalInfoCollapsed] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    watch
  } = useForm({
    resolver: yupResolver(createLeadSchema),
    defaultValues: {
      countryCode: getDefaultCountryCode(),
      accomodations: [],
      plotArea: [],
      project_accommodations: [],
      ...(initialValues || {})
    }
  });

  const PID = watch('interested_projects');
  const watchedCountryCode = watch('countryCode');
  const [projectAccommodationsKey, setProjectAccommodationsKey] = useState(0);

  useEffect(() => {
    if (!initialValues) return;
    reset({
      countryCode: getDefaultCountryCode(),
      accomodations: [],
      plotArea: [],
      project_accommodations: [],
      ...initialValues
    });
    if (initialValues.countryCode) {
      setSelectedCountryCode(initialValues.countryCode);
    }
  }, [initialValues, reset]);

  useEffect(() => {
    if (defaultProjectId != null && defaultProjectId !== '') {
      setValue('interested_projects', String(defaultProjectId));
    }
  }, [defaultProjectId, setValue]);

  // Cleanup timeout on unmount - DISABLED (auto-detection removed)
  // useEffect(() => {
  //   return () => {
  //     if (autoDetectTimeoutRef.current) {
  //         clearTimeout(autoDetectTimeoutRef.current);
  //     }
  //   };
  // }, []);

  // Get accommodations for the selected project
  const { data: Alaccommodations } = useQuery({
    queryKey: ['Alaccommodations', PID],
    queryFn: () => getAllAccommodationsByProject(PID),
    enabled: !!PID
  });

  const handleNameChange = useCallback(
    (value) => {
      const cleanedValue = value?.replace(/[^a-zA-Z0-9s. ]/g, '') || '';
      setValue('name', capitalizeWords(cleanedValue));
    },
    [setValue]
  );

  // Debounced auto-detection of country code - DISABLED
  // const autoDetectTimeoutRef = useRef(null);

  // const debouncedAutoDetect = useCallback(
  //   (phoneNumber) => {
  //     if (autoDetectTimeoutRef.current) {
  //       clearTimeout(autoDetectTimeoutRef.current);
  //     }

  //     autoDetectTimeoutRef.current = setTimeout(() => {
  //       if (phoneNumber.length >= 7) {
  //         const detectedCountry = detectCountryFromPhoneNumber(phoneNumber);
  //         if (detectedCountry && detectedCountry !== watchedCountryCode) {
  //           console.log(`Auto-detecting country from ${watchedCountryCode} to ${detectedCountry}`);
  //           setValue('countryCode', detectedCountry);
  //           setSelectedCountryCode(detectedCountry);
  //         }
  //       }
  //     }, 1000); // Wait 1 second after user stops typing
  //   },
  //   [watchedCountryCode, setValue]
  // );

  const handlePhoneNumberChange = useCallback(
    (value) => {
      const cleanedValue = cleanPhoneNumber(value || '');
      const phoneLength = getPhoneNumberLength(watchedCountryCode);
      const limitedValue = cleanedValue.slice(0, phoneLength.max);
      setValue('phone_number', limitedValue);

      // Trigger debounced auto-detection - DISABLED
      // debouncedAutoDetect(cleanedValue);
    },
    [setValue, watchedCountryCode]
  );

  const handleAlternatePhoneNumberChange = useCallback(
    (value) => {
      const cleanedValue = cleanPhoneNumber(value || '');
      const phoneLength = getPhoneNumberLength(watchedCountryCode);
      const limitedValue = cleanedValue.slice(0, phoneLength.max);
      setValue('alternate_phone_number', limitedValue);

      // Trigger debounced auto-detection for alternate phone number too - DISABLED
      // debouncedAutoDetect(cleanedValue);
    },
    [setValue, watchedCountryCode]
  );

  const handleProjectChange = useCallback(
    (value) => {
      setValue('interested_projects', value);
      setValue('project_accommodations', []);
      // Force re-render of project accommodations multiselect
      setProjectAccommodationsKey((prev) => prev + 1);
    },
    [setValue]
  );

  const handleCountryCodeChange = useCallback(
    (value) => {
      setValue('countryCode', value);
      setSelectedCountryCode(value);
      // Clear phone numbers when country changes as they may be invalid
      setValue('phone_number', '');
      setValue('alternate_phone_number', '');
    },
    [setValue]
  );

  const handleDateChange = (date) => {
    setValue('expected_clouser_date', date);
  };

  const transformedData =
    AllProjects?.map((item) => ({
      label: item?.name?.trim() || '',
      id: item?.id,
      value: item?.id
    })) || [];

  // Use dynamic country code options from the utility
  const countryCodeOptions = getCountryCodeOptions().map((country) => ({
    label: country.label,
    id: country.value,
    value: country.value,
    displayValue: country.displayValue // This will be shown in the input field
  }));

  const occupationOptions = [
    { value: 'Salaried', label: 'Salaried' },
    { value: 'Self employed', label: 'Self Employed' },
    { value: 'Business', label: 'Business' },
    { value: 'Govt employee', label: 'Government Employee' },
    { value: 'Others', label: 'Others' }
  ];

  const handleFormSubmit = (data) => {
    // Construct clean payload with only required fields
    const payload = {
      name: data.name,
      countryCode: `+${getCountryCallingCode(data.countryCode)}`, // Pass calling code like "+91" instead of "IN"
      phone_number: data.phone_number,
      interested_projects: [Number(data?.interested_projects)]
    };

    // Log the payload for debugging
    console.log('📤 AddLeadForm - Payload being sent:', {
      originalCountryCode: data.countryCode,
      callingCode: `+${getCountryCallingCode(data.countryCode)}`,
      fullPayload: payload
    });

    // Add optional fields only if they have values
    if (data.accomodations)
      payload.interestedAccomodationType =
        data?.accomodations?.map((item) => item.id || item) || [];
    if (data.plotArea) payload.plotArea = data?.plotArea?.map((item) => item.id || item) || [];
    if (data.project_accommodations)
      payload.accomodations = data?.project_accommodations?.map((item) => item.id || item) || [];
    if (data.reference_name) payload.reference_name = data.reference_name;
    if (data.email) payload.email = data.email;
    if (data.address) payload.address = data.address;
    if (data.alternate_phone_number) payload.alternate_phone_number = data.alternate_phone_number;
    if (data.occupation) payload.occupation = data.occupation;
    if (data.gender) payload.gender = data.gender;
    if (data.source) payload.source = data.source;
    if (data.expected_clouser_date) payload.expected_clouser_date = data.expected_clouser_date;
    if (data.expectedBudgetRange) payload.expectedBudgetRange = data.expectedBudgetRange;
    if (preLeadId != null && Number(preLeadId) > 0) {
      payload.pre_lead_id = Number(preLeadId);
    }

    onSubmit(payload);
  };

  console.log('countryCodeOptions', countryCodeOptions.length);

  // Get selected project details for display
  const getSelectedProject = () => {
    if (!PID) return null;
    return AllProjects?.find((project) => project.id === Number(PID));
  };
  const selectedProject = getSelectedProject();

  const plotAreaOptions = [
    {
      name: `0-100 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '0-100',
      value: '0-100'
    },
    {
      name: `100-200 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '100-200',
      value: '100-200'
    },
    {
      name: `200-300 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '200-300',
      value: '200-300'
    },
    {
      name: `300 - 500 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '300-500',
      value: '300-500'
    },
    {
      name: `500 - 750 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '500-750',
      value: '500-750'
    },
    {
      name: `750 - 900 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '750-900',
      value: '750-900'
    },
    {
      name: `900 - 1100 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '900-1100',
      value: '900-1100'
    },
    {
      name: `1100 - 1350 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '1100-1350',
      value: '1100-1350'
    },
    {
      name: `1350 - 1600 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '1350-1600',
      value: '1350-1600'
    },
    {
      name: `1600 - 2000 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '1600-2000',
      value: '1600-2000'
    },
    {
      name: `2000 - 2400 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '2000-2400',
      value: '2000-2400'
    },
    {
      name: `2400 - 3000 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '2400-3000',
      value: '2400-3000'
    },
    {
      name: `3000 - 3500 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '3000-3500',
      value: '3000-3500'
    },
    {
      name: `3500 - 4200 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '3500-4200',
      value: '3500-4200'
    },
    {
      name: `4200 - 5000 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '4200-5000',
      value: '4200-5000'
    },
    {
      name: `5000 - Above ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '5000-above',
      value: '5000-above'
    }
  ];

  return (
    <div className="flex flex-col justify-between items-start gap-4">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-4 pt-6 w-full">
        <div className="w-full rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Basic Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              width={100}
              label={'Name'}
              register={register}
              register_key={'name'}
              type={'text'}
              errors={errors?.name}
              isRequired={true}
              onType={(text) => handleNameChange(text)}
            />
            <div className="w-[100%] grid grid-cols-[140px_1fr] gap-2 items-end">
              <FormInput
                width={100}
                label={'Phone Number'}
                register={register}
                register_key={'countryCode'}
                type={'CountyCodedropdown'}
                isRequired={true}
                options={countryCodeOptions}
                errors={errors?.countryCode}
                onDropdownChange={handleCountryCodeChange}
                valuePre={watchedCountryCode}
                defaultValuePre={getDefaultCountryCode()}
              />
              <FormInput
                width={100}
                label={''}
                register={register}
                register_key={'phone_number'}
                type={'text'}
                errors={errors?.phone_number}
                onType={(text) => handlePhoneNumberChange(text)}
                isRequired={true}
                hideAstrik={true}
                placeholder={`Enter (${getPhoneNumberLength(watchedCountryCode).min}-${getPhoneNumberLength(watchedCountryCode).max} digits)`}
              />
            </div>
            <div className="w-[100%]">
              {lockProject && selectedProject ? (
                <div>
                  <label className="text-gray-600 mb-1 block">Interested Project</label>
                  <p className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
                    {selectedProject.name || selectedProject.label}
                  </p>
                </div>
              ) : (
                <FormInput
                  width={100}
                  label={'Interested Projects'}
                  register={register}
                  register_key={'interested_projects'}
                  defaultValuePre={''}
                  type={'dropdown'}
                  isRequired={true}
                  options={[
                    { label: 'Select Project', id: '', value: '', selected: true, disabled: true },
                    ...transformedData
                  ]}
                  errors={errors?.gender}
                  onDropdownChange={handleProjectChange}
                />
              )}

              {errors?.interested_projects && (
                <span className="text-red-500">{errors?.interested_projects?.message}</span>
              )}
            </div>
            <div className="w-[100%] flex flex-col gap-1">
              <label className="text-gray-600 mb-1 block">Project Accommodations</label>
              <Controller
                name="project_accommodations"
                control={control}
                render={({ field }) => (
                  <Multiselect
                    key={projectAccommodationsKey}
                    options={Alaccommodations || []}
                    selectedValues={field.value}
                    onSelect={(selectedList) => field.onChange(selectedList)}
                    onRemove={(selectedList) => field.onChange(selectedList)}
                    displayValue="name"
                    placeholder={PID ? 'Select project accommodations' : 'Select a project first'}
                    disabled={!PID}
                    showCheckbox
                    style={{
                      chips: {
                        background: '#2A565E',
                        color: 'white'
                      },
                      searchBox: {
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '8px',
                        background: PID ? 'white' : '#f5f5f5',
                        color: '#333'
                      },
                      input: {
                        color: '#333'
                      },
                      singleValue: {
                        color: '#333'
                      },
                      placeholder: {
                        color: '#6c757d'
                      },
                      option: {
                        color: '#333',
                        background: 'white'
                      },
                      listItem: {
                        color: '#333',
                        background: 'white'
                      }
                    }}
                  />
                )}
              />
              {errors.project_accommodations && (
                <span className="text-red-500">{errors.project_accommodations.message}</span>
              )}
            </div>
            <div className="w-[100%] flex flex-col gap-1">
              <label className="text-gray-600 mb-1 block">Interested Accomodation</label>
              <Controller
                name="accomodations"
                control={control}
                render={({ field }) => (
                  <Multiselect
                    options={[
                      { name: '1BHK', id: '1BHK', value: '1BHK' },
                      { name: '1.5BHK', id: '1.5BHK', value: '1.5BHK' },
                      { name: '2BHK', id: '2BHK', value: '2BHK' },
                      { name: '2.5BHK', id: '2.5BHK', value: '2.5BHK' },
                      { name: '3BHK', id: '3BHK', value: '3BHK' },
                      { name: '3.5BHK', id: '3.5BHK', value: '3.5BHK' },
                      { name: '4BHK', id: '4BHK', value: '4BHK' },
                      { name: '4.5BHK', id: '4.5BHK', value: '4.5BHK' },
                      { name: '5BHK', id: '5BHK', value: '5BHK' },
                      { name: '5.5BHK', id: '5.5BHK', value: '5.5BHK' },
                      { name: 'Other', id: 'Other', value: 'Other' }
                    ]}
                    selectedValues={field.value}
                    onSelect={(selectedList) => field.onChange(selectedList)}
                    onRemove={(selectedList) => field.onChange(selectedList)}
                    displayValue="name"
                    placeholder="Select Accommodations"
                    showCheckbox
                  />
                )}
              />
              {errors.accomodations && (
                <span className="text-red-500">{errors.accomodations.message}</span>
              )}
            </div>

            <div className="w-[100%] flex flex-col gap-1">
              <label className="text-gray-600 mb-1 block">
                Interested Plot Area{' '}
                {selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''}
              </label>
              <Controller
                name="plotArea"
                control={control}
                render={({ field }) => (
                  <Multiselect
                    options={plotAreaOptions}
                    selectedValues={field.value}
                    onSelect={(selectedList) => field.onChange(selectedList)}
                    onRemove={(selectedList) => field.onChange(selectedList)}
                    displayValue="name"
                    placeholder="Select Plot Area"
                    showCheckbox
                  />
                )}
              />
              {errors.plotArea && <span className="text-red-500">{errors.plotArea.message}</span>}
            </div>
          </div>
        </div>

        <div className="w-full rounded-lg border border-gray-200 bg-white p-4">
          <button
            type="button"
            className="w-full flex items-center justify-between text-left"
            onClick={() => setIsAdditionalInfoCollapsed((prev) => !prev)}>
            <h3 className="text-base font-semibold text-gray-800">Additional Information</h3>
            <span className="text-lg font-semibold text-gray-600 leading-none">
              {isAdditionalInfoCollapsed ? '+' : '-'}
            </span>
          </button>
          {!isAdditionalInfoCollapsed && <div className="grid grid-cols-2 gap-4 mt-4">
            <FormInput
              width={100}
              label={'Nick Name'}
              register={register}
              register_key={'reference_name'}
              type={'text'}
              errors={errors?.reference_name}
              onType={(text) => setValue('reference_name', capitalizeWords(text))}
              isRequired={false}
            />

            <FormInput
              width={100}
              label={'Alternate Phone Number'}
              register={register}
              register_key={'alternate_phone_number'}
              onType={(text) => handleAlternatePhoneNumberChange(text)}
              type={'text'}
              errors={errors?.alternate_phone_number}
              placeholder={`Enter alternate phone number (${getPhoneNumberLength(watchedCountryCode).min}-${getPhoneNumberLength(watchedCountryCode).max} digits)`}
            />
            <FormInput
              width={100}
              label={'Email'}
              register={register}
              register_key={'email'}
              type={'text'}
              errors={errors?.email}
            />
            <FormInput
              width={100}
              label={'Gender'}
              register={register}
              register_key={'gender'}
              defaultValuePre={''}
              type={'dropdown'}
              isRequired={false}
              options={[
                { value: '', label: 'Select Gender', disabled: true, selected: true },
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'other', label: 'Other' }
              ]}
              errors={errors?.gender}
            />

            <FormInput
              width={100}
              label={'Expected Budget '}
              register={register}
              register_key={'expectedBudgetRange'}
              type={'dropdown'}
              options={[
                { value: '', label: 'Select Budget', disabled: true, selected: true },
                { value: '0-10', label: '0 - 10 Lacs' },
                { value: '10-20', label: '10 Lacs - 20 Lacs' },
                { value: '20-30', label: '20 Lacs - 30 Lacs' },
                { value: '30-40', label: '30 Lacs - 40 Lacs' },
                { value: '40-50', label: '40 Lacs - 50 Lacs' },
                { value: '50-60', label: '50 Lacs - 60 Lacs' },
                { value: '60-75', label: '60 Lacs - 75 Lacs' },
                { value: '75-100', label: '75 Lacs - 100 Lacs' },
                { value: '100-150', label: '100 Lacs - 150 Lacs' },
                { value: '150-200', label: '150 Lacs - 200 Lacs' },
                { value: '200-300', label: '200 Lacs - 300 Lacs' },
                { value: '300-400', label: '300 Lacs - 400 Lacs' },
                { value: '400-above', label: '400 Lacs - Above' }
              ]}
              errors={errors?.expectedBudgetRange}
              isRequired={false}
            />

            <FormInput
              width={100}
              label={'Source'}
              register={register}
              register_key={'source'}
              defaultValuePre={''}
              type={'dropdown'}
              isRequired={false}
              options={[
                { value: '', label: 'Select Source', disabled: true, selected: true },
                { value: 'Real Estate Brokers/Agents', label: 'Real Estate Brokers/Agents' },
                { value: 'Channel Partners', label: 'Channel Partners' },
                { value: 'Property Listing Portals', label: 'Property Listing Portals' },
                { value: 'Google Ads', label: 'Google Ads' },
                { value: 'Facebook', label: 'Facebook' },
                { value: 'Instagram', label: 'Instagram' },
                { value: 'LinkedIn', label: 'LinkedIn' },
                { value: 'Company Website', label: 'Company Website' },
                {
                  value: 'Walk-ins from Project Signage/Hoardings',
                  label: 'Walk-ins from Project Signage/Hoardings'
                },
                {
                  value: 'Existing Customer Referrals (Word of Mouth)',
                  label: 'Existing Customer Referrals (Word of Mouth)'
                },
                { value: 'Friends/Family', label: 'Friends/Family' },
                { value: 'Tele-calling Campaigns', label: 'Tele-calling Campaigns' },
                { value: 'Email or WhatsApp Campaigns', label: 'Email or WhatsApp Campaigns' },
                { value: 'Offline Events & Exhibitions', label: 'Offline Events & Exhibitions' }
              ]}
              errors={errors?.source}
            />

            <FormInput
              width={100}
              register={register}
              register_key={'expected_clouser_date'}
              label={'Expected Clouser Date'}
              type={'date'}
              isRequired={false}
              onType={handleDateChange}
            />

            <FormInput
              width={100}
              label={'Occupation'}
              register={register}
              register_key={'occupation'}
              isRequired={false}
              defaultValuePre={''}
              type={'dropdown'}
              options={[
                { value: '', label: 'Select Occupation', disabled: true, selected: true },
                ...occupationOptions
              ]}
              errors={errors?.occupation}
            />
            <FormInput
              width={100}
              label={'Address'}
              register={register}
              register_key={'address'}
              type={'text'}
              errors={errors?.address}
            />
          </div>}
        </div>
        <div className="flex justify-end items-center space-x-4 mt-4">
          <Button type="button" onClick={onCancel} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            Create Lead
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddLeadForm;
